import { useEffect, useRef, useState, useCallback } from 'react';
import AgoraRTC, { ILocalVideoTrack } from 'agora-rtc-sdk-ng';
import VirtualBackgroundExtension, {
  IVirtualBackgroundProcessor
} from 'agora-extension-virtual-background';
import { toast } from 'react-toastify';
import useStore from '@/store/CreateStore';

const VideoTile = () => {
  const [loading, setLoading] = useState(false);
  const videoOn = useStore((state) => state.whiteboard.video);

  const videoTrackRef = useRef<ILocalVideoTrack | null>(null);
  const processorRef = useRef<IVirtualBackgroundProcessor | null>(null);
  const videoContainerRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    const initExtension = async () => {
      try {
        const extension = new VirtualBackgroundExtension();
        if (!extension.checkCompatibility()) {
          toast.error('Virtual background not supported on this device.');
          return;
        }

        AgoraRTC.registerExtensions([extension]);
        const processor = extension.createProcessor();
        await processor.init();
        processor.setOptions({
          type: 'none'
        });
        processorRef.current = processor;
      } catch (err) {
        console.error('Error initializing virtual background:', err);
      }
    };
    initExtension();

    return () => {
      videoTrackRef.current?.stop();
      videoTrackRef.current?.close();
    };
  }, []);

  const handleCamera = useCallback(
    async (turnOn: boolean) => {
      if (loading || !videoContainerRef.current) return;
      setLoading(true);

      if (!turnOn) {
        try {
          if (processorRef.current?.enabled) await processorRef.current.disable();
          videoTrackRef.current?.unpipe();
        } catch (err) {
          console.warn('Error disabling background:', err);
        }

        videoTrackRef.current?.stop();
        videoTrackRef.current?.close();
        videoTrackRef.current = null;
        setLoading(false);
        return;
      }

      try {
        const track = await AgoraRTC.createCameraVideoTrack({
          encoderConfig: '1440p_1',
          optimizationMode: 'motion'
        });

        if (processorRef.current) {
          track.pipe(processorRef.current).pipe(track.processorDestination);
          await processorRef.current.enable();
        }

        videoTrackRef.current = track;
        track.play(videoContainerRef.current);
      } catch (err) {
        console.error('Camera permission error:', err);
        toast.error('Please allow camera access in browser settings.');
      } finally {
        setLoading(false);
      }
    },
    [loading]
  );

  useEffect(() => {
    handleCamera(videoOn);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [videoOn]);

  return (
    <div
      ref={videoContainerRef}
      id="whiteboard-local-video"
      className="relative aspect-video w-full overflow-hidden bg-transparent"
    />
  );
};

export default VideoTile;
